import axios from 'axios';
import { API_CONFIG } from '../config/api';
import { UserRole } from '../types/user';

interface LoginResponse {
  token: string;
  userId: string;
  roleId: number; // Backend returns roleId as number
  isNewUser: boolean;
}

interface GoogleAuthResponse {
  token: string;
  userId: string;
  roleId: number; // Backend returns roleId as number
  isNewUser: boolean;
}

interface GoogleClientIdResponse {
  clientId?: string;  // For lowercase from previous backend
  ClientId?: string;  // For uppercase from current C# backend
}

class AuthService {
  private authEndpoint = `${API_CONFIG.baseURL}/api/auth`;
  private settingsEndpoint = `${API_CONFIG.baseURL}/api/settings`;
  private googleClientId: string | null = null;

  // Check if user is authenticated and token is not expired
  isAuthenticated(): boolean {
    const token = localStorage.getItem('token');
    if (!token) return false;
    
    try {
      // Parse the JWT token to check expiration
      const payload = JSON.parse(atob(token.split('.')[1]));
      const currentTime = Date.now() / 1000;
      
      // Check if token is expired
      if (payload.exp && payload.exp < currentTime) {
        // Token is expired, clear it
        this.clearToken();
        return false;
      }
      
      return true;
    } catch (_error) {
      // Token is malformed, clear it
      this.clearToken();
      return false;
    }
  }

  // Get user role - convert from stored roleId to UserRole enum
  // Expected consistent mapping: 0=NoRole (not selected), 1=Interviewee, 2=Interviewer
  getRole(): UserRole | null {
    const roleId = localStorage.getItem('userRoleId');
    if (!roleId) return null;
    
    const roleIdNumber = parseInt(roleId);
    
    // Return null if role not selected (0 or invalid)
    if (roleIdNumber === 0 || isNaN(roleIdNumber)) return null;
    
    // Direct mapping: 1 = Interviewee, 2 = Interviewer
    return roleIdNumber === 2 ? UserRole.Interviewer : UserRole.Interviewee;
  }

  // Get user ID
  getUserId(): string | null {
    return localStorage.getItem('userId');
  }

  // Store token and user info - store roleId as number
  setToken(token: string, userId: string, roleId: number): void {
    localStorage.setItem('token', token);
    localStorage.setItem('userId', userId);
    localStorage.setItem('userRoleId', roleId.toString());
  }

  // Clear auth data
  clearToken(): void {
    localStorage.removeItem('token');
    localStorage.removeItem('userId');
    localStorage.removeItem('userRoleId');
  }

  // Update user role in local storage
  updateRole(roleId: number): void {
    localStorage.setItem('userRoleId', roleId.toString());
  }

  // Get Google Client ID from backend
  async getGoogleClientId(): Promise<string> {
    // Return from cache if available
    if (this.googleClientId) {
      return this.googleClientId;
    }
    
    try {
      const response = await axios.get<GoogleClientIdResponse>(`${this.settingsEndpoint}/auth/google-client-id`);
      
      // Check if response.data exists and is an object
      if (!response.data || typeof response.data !== 'object') {
        throw new Error('Invalid response format from server');
      }
      
      const clientId = response.data.clientId || response.data.ClientId;
      
      if (!clientId || typeof clientId !== 'string') {
        throw new Error('Google Client ID not found in response or invalid format');
      }
      
      // Check if it's a placeholder value
      if (clientId.includes('YOUR_PRODUCTION_GOOGLE_CLIENT_ID') || clientId.includes('placeholder')) {
        throw new Error('Google Client ID is not properly configured - found placeholder value');
      }
      
      this.googleClientId = clientId;
      return this.googleClientId;
    } catch (error) {
      console.error('Failed to get Google Client ID:', error);
      
      // If it's an axios error, provide more specific error message
      if (axios.isAxiosError(error)) {
        const errorMessage = error.response?.data?.message || 'Failed to fetch Google Client ID from server';
        throw new Error(errorMessage);
      }
      
      throw new Error('Google authentication is not configured properly');
    }
  }

  // Register a new user
  async register(email: string, password: string, role: UserRole): Promise<LoginResponse> {
    try {
      const response = await axios.post<LoginResponse>(`${this.authEndpoint}/register`, {
        username: email, // Using email as username
        email,
        password,
        roleId: role // Send roleId as number (1 or 2)
      });

      this.setToken(response.data.token, response.data.userId, response.data.roleId);
      return response.data;
    } catch (error) {
      console.error('Registration failed:', error);
      throw error;
    }
  }

  // Log in a user
  async login(username: string, password: string): Promise<LoginResponse> {
    try {
      const response = await axios.post<LoginResponse>(`${this.authEndpoint}/login`, {
        username,
        password
      });

      this.setToken(response.data.token, response.data.userId, response.data.roleId);
      return response.data;
    } catch (error) {
      console.error('Login failed:', error);
      throw error;
    }
  }

  // Google authentication
  async googleLogin(idToken: string, role?: UserRole): Promise<GoogleAuthResponse> {
    try {
      const response = await axios.post<GoogleAuthResponse>(`${this.authEndpoint}/google`, {
        idToken,
        role
      });

      this.setToken(response.data.token, response.data.userId, response.data.roleId);
      return response.data;
    } catch (error) {
      console.error('Google login failed:', error);
      throw error;
    }
  }

  // Logout
  logout(): void {
    this.clearToken();
    window.location.href = '/login';
  }
}

export const authService = new AuthService(); 